// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction } from "@fluentui/react";

import {
  AppTheme,
  AppThemeManager,
  isSharedUXDarkModeOn,
  isThemingEnabled,
} from "@1js/acui-theme";

import { defaultAppColorPalette } from "../styling/appColorPalette";
import { controlPalette } from "./controlPalette";

export interface StandardControlSemanticColors {
  defaultBackgroundColor: string;
  disabledBackgroundColor: string;
  hoverBackgroundColor: string;
  toggledBackgroundColor: string;
  selectedBackgroundColor: string;
  languageDialogBackgroundChecked: string;
  expandedBackgroundColor: string;
  pressedBackgroundColor: string;
  focusedBackgroundColor: string;
  focusDisabledBackgroundColor: string;
  borderColor: string;
  focusedBorderColor: string;
  selectedBorderColor: string;
  disabledBorderColor: string;
  textColor: string;
  textSecondaryColor: string;
  textDisabledColor: string;
  textDisclaimerColor: string;
  menuFocusedBackgroundColor: string;
  inputBackgroundColor: string;
  inputBorderColor: string;
  inputHoverBorderColor: string;
  inputFocusBorderColor: string;
  inputPressedBorderColor: string;
  inputPressedBackgroundColor: string;
  inputTextDisabledColor: string;
  inputSelectionTextColor: string;
  inputSelectionBackgroundColor: string;
  inputTextColor: string;
  inputButtonHoverBackground: string;
  inputButtonPressedBackgroundColor: string;
  checkBoxBorderColor: string;
  checkBoxHoverBackgroundColor: string;
  checkBoxHoverCheckmarkColor: string;
  checkBoxPressedBackgroundColor: string;
  checkMarkSelectedColor: string;
  checkMarkCheckDisableBackgroundColor: string;
  checkMarkColor: string;
  checkMarkDisabledColor: string;
  checkBoxFocusedTextColor: string;
  comboBoxButtonHoverBackgroundColor: string;
  comboBoxButtonSelectedBackgroundColor: string;
  spinButtonArrowBoxFocusedBackgroundColor: string;
  spinButtonContainerContainerHoverBackground: string;
  menuBackgroundColor: string;
  menuItemTextColor: string;
  menuDividerBackgroundColor: string;
  textFieldHoverBorderColor: string;
  textFieldLabelColor: string;
  defaultGalleryBackgroundColor: string;
  fontGalleryBackgroundColor: string;
  tableGalleryBackgroundColor: string;
  galleryExpandButtonDefaultBackgroundColor: string;
  galleryHoverFocusColor: string;
  galleryPressedBackgroundColor: string;
  gallerySelectedColor: string;
  galleryTextColor: string;
  fontGalleryTextColor: string;
  fontGalleryTextDisabledColor: string;
  tableGalleryCellColor: string;
  linkDisabledColor: string;
  cellHighlightBackgroundColor: string;
  cellHighlightBorderColor: string;
  peripheralSelectedBackgroundColor: string;
  peripheralFocusedOutlineColor: string;
  warningIconColor: string;
  colorPickerWhiteCellBorderColor: string;
  colorPickerDisabledCellColor: string;
  colorPickerSelectedBorderColor: string;
  dialogBackgroundColor: string;
}

const standardControlSemanticColors: StandardControlSemanticColors = {
  /**
   * Default color for controls at rest
   */
  defaultBackgroundColor: controlPalette ? defaultAppColorPalette.white : "",
  /**
   * Background color of a disabled control
   */
  disabledBackgroundColor: defaultAppColorPalette.gray20,

  /**
   * Background color for when a control is hovered
   */
  hoverBackgroundColor: defaultAppColorPalette.gray20,

  /**
   * Background color for when a control is toggled on.
   */
  toggledBackgroundColor: defaultAppColorPalette.gray30,

  /**
   * Background color for when controls are selected. Ex: options in a comboBox, options in a checklist.
   * This is not the same as a focused/hovered state. A selected option in a comboBox would also change the
   * value in the comboBox's input field. That is not necessarily the case for a hovered option.
   */
  selectedBackgroundColor: defaultAppColorPalette.gray30,

  /**
   * This color is the background color for selected and hovered items in the selection menu of the
   * Language Dialog. A new variable was created, since this color should remain the light version
   * even in dark mode.
   */
  languageDialogBackgroundChecked: defaultAppColorPalette.gray130,

  /**
   * "Expanded" is often used in the same context as "selected" in redlines. In our styles, it refers to the
   * state of a control that is responsible for showing/hiding a contextual menu or gallery. It is in the
   * expanded state when the related contextual menu or gallery is visible
   */
  expandedBackgroundColor: defaultAppColorPalette.gray30,

  /**
   * Background color of a control being pressed via a mouse down
   */
  pressedBackgroundColor: defaultAppColorPalette.gray40,

  focusedBackgroundColor: defaultAppColorPalette.white,

  focusDisabledBackgroundColor: defaultAppColorPalette.gray20,

  borderColor: defaultAppColorPalette.gray60,

  /**
   * The color to use for the border on a control that has focus
   */
  focusedBorderColor: defaultAppColorPalette.black,

  selectedBorderColor: defaultAppColorPalette.gray110,

  menuFocusedBackgroundColor: defaultAppColorPalette.gray30,

  disabledBorderColor: defaultAppColorPalette.gray20,

  textColor: defaultAppColorPalette.gray180,

  /**
   * Description text color of compound button.
   */
  textSecondaryColor: defaultAppColorPalette.gray130,
  textDisabledColor: defaultAppColorPalette.gray70,
  textDisclaimerColor: defaultAppColorPalette.gray90,

  /**
   * General shared colors for the CheckBox, ComboBox, and SpinButton
   */
  inputBackgroundColor: defaultAppColorPalette.white,
  inputBorderColor: defaultAppColorPalette.gray110,
  inputHoverBorderColor: defaultAppColorPalette.gray120,
  inputFocusBorderColor: defaultAppColorPalette.gray130,
  inputPressedBorderColor: defaultAppColorPalette.gray60, // Task 3451776: Combine with InputBorder after New Redline BRS cleanup
  inputPressedBackgroundColor: defaultAppColorPalette.gray80,
  inputTextDisabledColor: defaultAppColorPalette.gray60,
  inputSelectionTextColor: defaultAppColorPalette.white,
  inputSelectionBackgroundColor: defaultAppColorPalette.gray130,
  inputTextColor: defaultAppColorPalette.gray180,
  inputButtonHoverBackground: defaultAppColorPalette.gray40,
  inputButtonPressedBackgroundColor: defaultAppColorPalette.gray50,

  checkBoxBorderColor: defaultAppColorPalette.gray110,
  checkBoxHoverBackgroundColor: controlPalette.hoverBackgroundColor,
  checkBoxHoverCheckmarkColor: controlPalette.hoverBackgroundColor,
  checkBoxPressedBackgroundColor: defaultAppColorPalette.gray50,
  checkMarkSelectedColor: defaultAppColorPalette.gray180,
  checkMarkCheckDisableBackgroundColor: defaultAppColorPalette.gray60,
  checkMarkDisabledColor: defaultAppColorPalette.white,
  checkBoxFocusedTextColor: defaultAppColorPalette.black,
  checkMarkColor: defaultAppColorPalette.gray180,

  comboBoxButtonHoverBackgroundColor: defaultAppColorPalette.gray30,
  comboBoxButtonSelectedBackgroundColor: defaultAppColorPalette.gray40,

  spinButtonArrowBoxFocusedBackgroundColor: defaultAppColorPalette.gray20,
  spinButtonContainerContainerHoverBackground: defaultAppColorPalette.gray20,

  /**
   * Menu Colors
   */
  menuBackgroundColor: defaultAppColorPalette.white,
  menuItemTextColor: defaultAppColorPalette.gray180,
  menuDividerBackgroundColor: defaultAppColorPalette.gray60,

  textFieldHoverBorderColor: defaultAppColorPalette.gray80,
  textFieldLabelColor: defaultAppColorPalette.gray160,

  /**
   * Gallery colors
   */
  defaultGalleryBackgroundColor: defaultAppColorPalette.white,
  fontGalleryBackgroundColor: defaultAppColorPalette.white,
  tableGalleryBackgroundColor: defaultAppColorPalette.white,
  galleryExpandButtonDefaultBackgroundColor: defaultAppColorPalette.gray20,
  galleryHoverFocusColor: defaultAppColorPalette.gray40,
  galleryPressedBackgroundColor: defaultAppColorPalette.gray60,
  gallerySelectedColor: defaultAppColorPalette.gray50,
  galleryTextColor: defaultAppColorPalette.black,
  fontGalleryTextColor: defaultAppColorPalette.black,
  fontGalleryTextDisabledColor: defaultAppColorPalette.gray70,
  tableGalleryCellColor: defaultAppColorPalette.black,

  /**
   * Link colors
   */
  linkDisabledColor: defaultAppColorPalette.gray70,

  /**
   * Cell colors
   */
  cellHighlightBackgroundColor: defaultAppColorPalette.gray50,
  cellHighlightBorderColor: defaultAppColorPalette.gray160,

  /**
   * Peripheral button colors
   */
  peripheralSelectedBackgroundColor: defaultAppColorPalette.gray50,
  peripheralFocusedOutlineColor: defaultAppColorPalette.black,

  /**
   * Error UI
   */
  warningIconColor: defaultAppColorPalette.gray120,

  /**
   * Color picker
   */
  colorPickerWhiteCellBorderColor: defaultAppColorPalette.gray50,
  colorPickerDisabledCellColor: defaultAppColorPalette.gray60,
  colorPickerSelectedBorderColor: defaultAppColorPalette.gray110,

  /**
   * Dialogs
   */
  dialogBackgroundColor: controlPalette ? defaultAppColorPalette.white : "",
};

const standardControlSemanticColorsWithTheme = memoizeFunction(
  (theme: ITheme): StandardControlSemanticColors => {
    return {
      /**
       * Default color for controls at rest
       */
      defaultBackgroundColor: theme.palette.white,
      /**
       * Background color of a disabled control
       */
      disabledBackgroundColor: theme.palette.neutralLighterAlt,

      /**
       * Background color for when a control is hovered
       */
      hoverBackgroundColor: theme.palette.neutralLighterAlt,

      /**
       * Background color for when a control is toggled on.
       */
      toggledBackgroundColor: theme.palette.neutralLighter,

      /**
       * Background color for when controls are selected. Ex: options in a comboBox, options in a checklist.
       * This is not the same as a focused/hovered state. A selected option in a comboBox would also change the
       * value in the comboBox's input field. That is not necessarily the case for a hovered option.
       */
      selectedBackgroundColor: theme.palette.neutralLighter,

      /**
       * This color is the background color for selected and hovered items in the selection menu of the
       * Language Dialog. A new variable was created, since this color should remain the light version
       * even in dark mode.
       */
      languageDialogBackgroundChecked: theme.palette.neutralPrimaryAlt,

      /**
       * "Expanded" is often used in the same context as "selected" in redlines. In our styles, it refers to the
       * state of a control that is responsible for showing/hiding a contextual menu or gallery. It is in the
       * expanded state when the related contextual menu or gallery is visible
       */
      expandedBackgroundColor: theme.palette.neutralLighter,

      /**
       * Background color of a control being pressed via a mouse down
       */
      pressedBackgroundColor: theme.palette.neutralLight,

      focusedBackgroundColor: theme.palette.white,

      focusDisabledBackgroundColor: theme.palette.neutralLighterAlt,

      borderColor: theme.palette.neutralQuaternary,

      /**
       * The color to use for the border on a control that has focus
       */
      focusedBorderColor: theme.palette.black,

      selectedBorderColor: theme.palette.neutralSecondaryAlt,

      menuFocusedBackgroundColor: theme.palette.neutralLighter,

      disabledBorderColor: theme.palette.neutralLighterAlt,

      textColor: theme.palette.neutralDark,

      /**
       * Description text color of compound button.
       */
      textSecondaryColor: theme.palette.neutralPrimaryAlt,
      textDisabledColor: theme.palette.neutralTertiaryAlt,
      textDisclaimerColor: theme.palette.neutralTertiary,

      /**
       * General shared colors for the CheckBox, ComboBox, and SpinButton
       */
      inputBackgroundColor: theme.palette.white,
      inputBorderColor: theme.palette.neutralSecondaryAlt,
      inputHoverBorderColor: theme.palette.neutralSecondary,
      inputFocusBorderColor: theme.palette.neutralPrimaryAlt,
      inputPressedBorderColor: theme.palette.neutralQuaternary, // Task 3451776: Combine with InputBorder after New Redline BRS cleanup
      inputPressedBackgroundColor: theme.palette.neutralTertiaryAlt,
      inputTextDisabledColor: theme.palette.neutralQuaternary,
      inputSelectionTextColor: theme.palette.white,
      inputSelectionBackgroundColor: theme.palette.neutralPrimaryAlt,
      inputTextColor: theme.palette.neutralDark,
      inputButtonHoverBackground: theme.palette.neutralLight,
      inputButtonPressedBackgroundColor: theme.palette.neutralQuaternaryAlt,

      checkBoxBorderColor: theme.palette.neutralSecondaryAlt,
      checkBoxHoverBackgroundColor: theme.palette.neutralLight,
      checkBoxHoverCheckmarkColor: theme.palette.neutralLight,
      checkBoxPressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      checkMarkSelectedColor: theme.palette.neutralDark,
      checkMarkCheckDisableBackgroundColor: theme.palette.neutralQuaternary,
      checkMarkDisabledColor: theme.palette.white,
      checkBoxFocusedTextColor: theme.palette.black,
      checkMarkColor: theme.palette.neutralDark,

      comboBoxButtonHoverBackgroundColor: theme.palette.neutralLighter,
      comboBoxButtonSelectedBackgroundColor: theme.palette.neutralLight,

      spinButtonArrowBoxFocusedBackgroundColor: theme.palette.neutralLighterAlt,
      spinButtonContainerContainerHoverBackground:
        theme.palette.neutralLighterAlt,

      /**
       * Menu Colors
       */
      menuBackgroundColor: theme.palette.white,
      menuItemTextColor: theme.palette.neutralDark,
      menuDividerBackgroundColor: theme.palette.neutralQuaternary,

      textFieldHoverBorderColor: theme.palette.neutralTertiaryAlt,
      textFieldLabelColor: theme.palette.neutralPrimary,

      /**
       * Gallery colors
       */
      defaultGalleryBackgroundColor: theme.palette.white,
      fontGalleryBackgroundColor: theme.palette.white,
      tableGalleryBackgroundColor: theme.palette.white,
      galleryExpandButtonDefaultBackgroundColor:
        theme.palette.neutralLighterAlt,
      galleryHoverFocusColor: theme.palette.neutralSecondary,
      galleryPressedBackgroundColor: theme.palette.neutralQuaternary,
      gallerySelectedColor: theme.palette.neutralSecondaryAlt,
      galleryTextColor: theme.palette.black,
      fontGalleryTextColor: theme.palette.black,
      fontGalleryTextDisabledColor: theme.palette.neutralTertiaryAlt,
      tableGalleryCellColor: theme.palette.black,

      /**
       * Link colors
       */
      linkDisabledColor: theme.palette.neutralTertiaryAlt,

      /**
       * Cell colors
       */
      cellHighlightBackgroundColor: theme.palette.neutralQuaternaryAlt,
      cellHighlightBorderColor: theme.palette.neutralPrimary,

      /**
       * Peripheral button colors
       */
      peripheralSelectedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      peripheralFocusedOutlineColor: theme.palette.black,

      /**
       * Error UI
       */
      // should be #C8C6C4 in dark mode but doesn't exist in dark theme currently
      warningIconColor: theme.palette.neutralSecondary,

      /**
       * Color picker
       */
      colorPickerWhiteCellBorderColor: theme.palette.neutralQuaternaryAlt,
      colorPickerDisabledCellColor: theme.palette.neutralQuaternary,
      colorPickerSelectedBorderColor: theme.palette.neutralSecondaryAlt,

      /**
       * Dialogs
       */
      dialogBackgroundColor: theme.palette.white,
    };
  }
);

const darkModeStandardControlSemanticColors = memoizeFunction(
  (theme: ITheme): StandardControlSemanticColors => {
    const lightTheme = AppThemeManager.getInstance().getThemeForMode(
      AppTheme.Light
    );
    return {
      ...standardControlSemanticColorsWithTheme(theme),
      fontGalleryBackgroundColor: lightTheme.palette.white,
      fontGalleryTextColor: lightTheme.palette.black,
      fontGalleryTextDisabledColor: lightTheme.palette.neutralTertiaryAlt,
      tableGalleryBackgroundColor: lightTheme.palette.white,
      tableGalleryCellColor: lightTheme.palette.black,
      menuBackgroundColor: theme.palette.neutralLight,
      gallerySelectedColor: theme.palette.neutralSecondary,
      galleryHoverFocusColor: theme.palette.neutralSecondaryAlt,
      defaultBackgroundColor: theme.palette.neutralLight,
      defaultGalleryBackgroundColor: theme.palette.neutralLight,
      dialogBackgroundColor: theme.palette.neutralLighter,
      languageDialogBackgroundChecked: lightTheme.palette.neutralPrimaryAlt,
    };
  }
);

export const getStandardControlSemanticColorsWithTheme = (
  theme: ITheme
): StandardControlSemanticColors => {
  if (!isThemingEnabled() || theme === null || theme === undefined) {
    return standardControlSemanticColors;
  }

  if (isSharedUXDarkModeOn()) {
    return darkModeStandardControlSemanticColors(theme);
  }

  return standardControlSemanticColorsWithTheme(theme);
};
